import type { LoginRequest } from '@jq4race/shared';

import { OPERATOR_ROLE, type Session } from '../../domain/entities/session';
import type { AuthRepository } from '../../domain/ports/auth-repository';
import type { SessionFactory } from '../ports/session-factory';

export class NonOperatorLoginError extends Error {
  constructor(public readonly role: string) {
    super('This app is for valets only. Use the admin console for your role.');
    this.name = 'NonOperatorLoginError';
  }
}

export type LoginResult = { kind: 'authenticated'; session: Session };

interface LoginUseCaseDeps {
  authRepository: AuthRepository;
  createSession: SessionFactory;
}

// Operator login flow for the PWA.
//
// ADR-010 sketches a PIN + device-enrolment flow for operators (roster from
// localStorage device_token, 4-digit PIN). For MVP we use the same
// email+password flow as admins because (a) JQ4-5 already provisions
// operators with a password via invitation, (b) the PIN+device flow needs
// the supervisor role + enrolment endpoints that don't ship until Sprint 2.
// Tracked as TD-007 in docs/tech-debt.md.
//
// MFA is not exercised here: ADR-010 + JQ4-4 exempt operators from MFA, so
// the backend never returns `mfaPending: true` for an operator. If it
// somehow does (e.g., misconfigured user), we treat it as a failure.
export async function loginUseCase(
  input: LoginRequest,
  deps: LoginUseCaseDeps,
): Promise<LoginResult> {
  const result = await deps.authRepository.login(input);

  if (result.mfaPending) {
    throw new NonOperatorLoginError('unknown');
  }

  const session = deps.createSession({ ...result, email: input.email });
  if (!session) throw new Error('invalid token in response');

  if (session.user.role !== OPERATOR_ROLE) {
    throw new NonOperatorLoginError(session.user.role);
  }

  return { kind: 'authenticated', session };
}
